import { useCallback, useMemo } from 'react';
import { orderBy } from 'lodash';
import { PINNED_FIRMS_KEY } from 'common/constants/userPreferences';
import { useLocalStorageByUser } from 'common/hooks';
import { Text } from 'components/ui';
import FirmOption from 'pages/Auth/components/FirmOption';
import type { FirmListProps, LoginFirm } from './FirmList.props';

interface PinnedFirmOption {
  value: number;
  label: string;
}

const FirmList = ({ firmList, searchState }: FirmListProps) => {
  const [pinnedFirms, setPinnedFirms] = useLocalStorageByUser(PINNED_FIRMS_KEY, []) as [
    PinnedFirmOption[],
    (value: PinnedFirmOption[]) => void,
  ];

  const updatePinStatus = useCallback(
    (id: number, title: string, isPinned: boolean) => {
      let nextPinnedOptions: PinnedFirmOption[];

      if (isPinned) {
        nextPinnedOptions = pinnedFirms.filter(option => option.value !== id) ?? [];
      } else {
        nextPinnedOptions = [...pinnedFirms, { value: id, label: title }];
      }

      setPinnedFirms(nextPinnedOptions);
    },
    [pinnedFirms, setPinnedFirms]
  );

  const renderFirmOption = (firm: LoginFirm) => (
    <FirmOption key={firm.id} iconUrl={firm.icon_url ?? undefined} {...firm} />
  );

  const processedFirmList = useMemo(() => {
    if (!firmList) {
      return [];
    }

    return firmList.map((firm: LoginFirm) => {
      const isFirmPinned = Boolean(pinnedFirms.find(pinnedFirm => pinnedFirm.value === firm.id));

      return {
        ...firm,
        isPinnable: true,
        isPinned: isFirmPinned,
        updatePinStatus: (id: number, title: string, isPinned: boolean) => updatePinStatus(id, title, isPinned),
      };
    });
  }, [firmList, pinnedFirms, updatePinStatus]);

  if (!firmList || firmList.length === 0) {
    return null;
  }

  // Sort by pinned status (pinned items first)
  const sortedFirms = orderBy(processedFirmList, ['isPinned'], ['desc']);

  if (!searchState) {
    // if Search is empty return the full list;
    return (
      <>
        {sortedFirms.map(renderFirmOption)}
      </>
    );
  }

  const filteredList = sortedFirms.filter((firm: LoginFirm) =>
    firm.name.toLowerCase().includes(searchState.toLowerCase())
  );

  if (filteredList.length === 0) {
    return <Text variant="h4">FIRM NOT FOUND</Text>;
  }

  return (
    <>
      {filteredList.map(renderFirmOption)}
    </>
  );
};

export default FirmList;
